import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import { sendAccountPasswordResetEmail } from 'hooks/public-account-requests';
import usePageTitle from 'hooks/use-page-title';

import LoginLayout from 'containers/login/login-layout';

import { Stack } from 'ui/stack';

import { Typography } from 'ui/typography';
import { Input } from 'ui/input';
import Button from 'components/partials/button/button';
import toast from 'components/partials/toast/toast';

// Bounded character classes prevent catastrophic backtracking; the schema also
// keeps `Yup.email()` for the spec-compliant base check.
const EMAIL_PATTERN = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,63}$/;

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Must be a valid email address')
    .matches(EMAIL_PATTERN, 'Must be a valid email address')
    .required('Email address is required'),
});

// `Yup.InferType` keeps the schema as the single source of truth and avoids
// manual-type/schema drift that breaks `yupResolver`'s generic inference.
type RequestPasswordFormValues = Yup.InferType<typeof validationSchema>;

const RequestPassword: React.FC = () => {
  usePageTitle('Reset Password');

  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
    },
  });

  const navigate = useNavigate();

  const onSubmit = async (values: RequestPasswordFormValues): Promise<void> => {
    try {
      await sendAccountPasswordResetEmail(values.email);
      toast.success({
        title: 'Password reset email sent',
        message: 'Check your email for a password reset link.',
      });

      navigate('/login');
    } catch (error) {
      toast.error({
        title: 'Error sending password reset email',
        message:
          (error as { response?: { data?: { detail?: string } } })?.response
            ?.data?.detail || 'Something went wrong.',
      });

      setError('email', {
        type: 'manual',
        message: 'Failed to send reset email. Please try again.',
      });
    }
  };

  return (
    <LoginLayout>
      <Typography
        data-testid="request-password-heading"
        variant="h1"
        sx={(theme) => ({
          mb: 2,
          textAlign: 'center',
          [theme.breakpoints.down('tablet')]: {
            fontSize: theme.typography.h3.fontSize,
            lineHeight: theme.typography.h3.lineHeight,
          },
        })}
      >
        Reset Password
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              label="Email"
              placeholder="Enter your email"
              error={!!error}
              errorMessage={error?.message}
              data-testid="reset-password_email-input"
              sx={{ mt: 1.5 }}
            />
          )}
        />

        <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 2 }}>
          <Button
            type="submit"
            variant="brand-filled"
            disabled={isSubmitting}
            className="w-full"
          >
            {isSubmitting ? 'Sending...' : 'Continue'}
          </Button>
        </Stack>
      </form>
    </LoginLayout>
  );
};

export default RequestPassword;
